import {
  BookOpen,
  ChartLine,
  CloudUpload,
  Contact,
  History,
  MessagesSquare,
  Newspaper,
  ShieldCheck,
  Sparkles,
  Table,
  ThumbsUp,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { Role } from '@/shared/session/roleStore';

export type NavGroup = 'Research' | 'Distribution' | 'Administration';

export type NavItem = {
  label: string;
  path: string;
  group: NavGroup;
  icon: LucideIcon;
  /** A count shown beside the label: questions waiting for the desk, drafts waiting for approval. */
  badge?: 'desk' | 'approvals';
  roles: readonly Role[];
  /** Outside a demo, the signed-in account's own role must be one of these too. */
  account?: readonly Role[];
};

const ALL: readonly Role[] = ['rm', 'analyst', 'admin'];

export const NAV_GROUPS: readonly NavGroup[] = ['Research', 'Distribution', 'Administration'];

export const NAV_ITEMS: readonly NavItem[] = [
  { label: 'Ask Research', path: '/', group: 'Research', icon: Sparkles, roles: ALL },
  { label: 'Research library', path: '/library', group: 'Research', icon: BookOpen, roles: ALL },
  { label: "What's new", path: '/whats-new', group: 'Research', icon: Newspaper, roles: ALL },
  { label: 'House views', path: '/house-views', group: 'Research', icon: ChartLine, roles: ALL },
  { label: 'Desk questions', path: '/desk', group: 'Research', icon: MessagesSquare, roles: ['analyst', 'admin'], badge: 'desk' },
  { label: 'Answer feedback', path: '/feedback', group: 'Research', icon: ThumbsUp, roles: ['analyst', 'admin'] },
  { label: 'My client lists', path: '/my-lists', group: 'Distribution', icon: Contact, roles: ['rm'], account: ['rm'] },
  { label: 'Approvals', path: '/approvals', group: 'Distribution', icon: ShieldCheck, roles: ['analyst'], badge: 'approvals' },
  { label: 'Audit log', path: '/audit', group: 'Distribution', icon: History, roles: ['analyst', 'admin'] },
  { label: 'Forecast table', path: '/forecasts', group: 'Administration', icon: Table, roles: ['admin'] },
  { label: 'Ingestion', path: '/ingestion', group: 'Administration', icon: CloudUpload, roles: ['admin'] },
  { label: 'Users', path: '/users', group: 'Administration', icon: UserCog, roles: ['admin'], account: ['admin'] },
  // Users and client contact details: an admin account once demo roles are off; in a demo the switcher opens them (the API does the same)
  { label: 'WhatsApp subscribers', path: '/subscribers', group: 'Administration', icon: Users, roles: ['admin'], account: ['admin'] },
];

export const rolesFor = (path: string): readonly Role[] =>
  NAV_ITEMS.find((item) => item.path === path)?.roles ?? ALL;

/** Account roles a page needs whatever the switcher shows; every account when the page does not say. */
export const accountRolesFor = (path: string): readonly Role[] =>
  NAV_ITEMS.find((item) => item.path === path)?.account ?? ALL;

/** Whether the signed-in account may open the page, before the acting role is considered. */
export const accountMayOpen = (path: string, accountRole: Role, demo: boolean): boolean => {
  const item = NAV_ITEMS.find((i) => i.path === path);
  if (!item?.account || item.account.includes(accountRole)) return true;
  return demo;
};

/** Home (Ask Research) matches only itself; the library also owns the report pages it links to. */
export const isActivePath = (itemPath: string, pathname: string) => {
  if (itemPath === '/') return pathname === '/';
  if (itemPath === '/library') return pathname.startsWith('/library') || pathname.startsWith('/documents');
  return pathname.startsWith(itemPath);
};

/** Full-bleed screens own the whole content area: no page padding or footer. Ask Research (the home page) and a
 * report's page, which lays out the PDF and its panel edge to edge. */
export const isFullBleed = (pathname: string) => pathname === '/' || pathname.startsWith('/documents/');

export const activeNavItem = (pathname: string) => NAV_ITEMS.find((item) => isActivePath(item.path, pathname));
